"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MetricsTracker = void 0;
exports.generateMetricsAttribute = generateMetricsAttribute;
var _environment = require("@sm/utils/dist/environment");
var _enums = _interopRequireDefault(require("./types/enums"));
var _BaseMetricsTracker = require("./BaseMetricsTracker");
var _DataAnalyticsMetricsSubscriber = _interopRequireDefault(require("./subscribers/DataAnalyticsMetricsSubscriber"));
var _LoggingMetricsSubscriber = _interopRequireDefault(require("./subscribers/LoggingMetricsSubscriber"));
var _GTMMeticsSubscriber = _interopRequireDefault(require("./subscribers/GTMMeticsSubscriber"));
var _FacebookPixelSubscriber = _interopRequireDefault(require("./subscribers/FacebookPixelSubscriber"));
var _AmplitudeSubscriber = require("./subscribers/AmplitudeSubscriber");
var _glob$SM; // fix for downstream tree-shaking
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var _getClientEnvironment = (0, _environment.getClientEnvironmentDetails)(),
  isBrowser = _getClientEnvironment.isBrowser;
var glob = isBrowser ? window : global;

// Import/require only in the browser
var delegatedEvents = isBrowser ? require('delegated-events') : null;

/**
 * Returns the amplitude subscriber on the metrics root object, creating
 * it if it doesn't already exist
 *
 * @param root The metrics root object
 * @returns The instantiated Amplitude subscriber method
 */
function getAmplitudeSubscriber(root) {
  if (!root._amplitudeSubscriber) {
    root._amplitudeSubscriber = (0, _AmplitudeSubscriber.createAmplitudeSubscriber)(root.config);
  }
  return root._amplitudeSubscriber;
}

/**
 * Generates a string of event data that can be used in an HTML attribute
 *
 * @param eventData The name and data of the event
 * @returns A formatted strong of the eventData passed in
 */
function generateMetricsAttribute(eventData) {
  return JSON.stringify({
    name: eventData.name || _enums.default.ELEMENT_INTERACTION,
    data: eventData.data
  });
}

// we want to ensure that there is only ever one MetricsTracker created and exported
// from this package, so export the global var if it's there, otherwise create it
var _MetricsTracker = glob === null || glob === void 0 || (_glob$SM = glob.SM) === null || _glob$SM === void 0 ? void 0 : _glob$SM.MetricsTracker;
if (!_MetricsTracker) {
  _MetricsTracker = _objectSpread(_objectSpread({}, (0, _BaseMetricsTracker.createMetricsTracker)(glob)), {}, {
    /**
     * Initializes the events and configuration of the MetricsTracker
     *
     * @override
     * @param config
     * @returns
     */
    initialize: function initialize(config) {
      var root = (0, _BaseMetricsTracker.setupRoot)(glob);
      root.config = config;
      root.digitalData = (0, _BaseMetricsTracker.getCleanDigitalData)(config.user.id);

      // amplitude initializes itself specially, so we'll init the subscriber here for persistence reasons
      root._amplitudeSubscriber = getAmplitudeSubscriber(root);
      setupEvents(root);
      this.isInitialized = true;
      return root;
    },
    reset: function reset() {
      var root = (0, _BaseMetricsTracker.getRoot)(glob);
      root.digitalData = (0, _BaseMetricsTracker.getCleanDigitalData)(root.config.user.id);
      root.subscribers = [];
      root.automaticSubscribers = [];

      // Add the mandatory dataAnalytics subscriber
      this.addSubscriber(_DataAnalyticsMetricsSubscriber.default);
      // Add the mandatory logging subscriber
      this.addSubscriber(_LoggingMetricsSubscriber.default);
      // Add Google Tag Manager dataLayer subscriber
      this.addSubscriber(_GTMMeticsSubscriber.default);
      // Add Facebook Pixel subscriber
      this.addSubscriber(_FacebookPixelSubscriber.default);
      // Re-add the Amplitude Subscriber (no need to create a new one)
      this.addSubscriber(getAmplitudeSubscriber(root));
      return root;
    }
  });

  // toss out to the global namespace
  if (isBrowser) {
    window.SM = window.SM || {};
    window.SM.MetricsTracker = _MetricsTracker;
  }
}
function setupEvents(root) {
  var _window;
  if (!isBrowser) {
    return;
  }

  // only create one event listener on the document
  // this makes it safe for consumer apps to call initialize multiple times
  if ((_window = window) !== null && _window !== void 0 && (_window = _window.SM) !== null && _window !== void 0 && _window.MetricsTracker && !window.SM.MetricsTracker.isInitialized) {
    var _MetricsTracker2 = window.SM.MetricsTracker;
    /**
     * Add on click event listener
     * Note - this is done in the getInstance method; hence, until this
     * method is called, the event handler would never be bound.
     */
    delegatedEvents.on('click', '[data-sm-metrics]', function gce(event) {
      if (event && event.currentTarget) {
        var metricsData = event.currentTarget.getAttribute('data-sm-metrics');
        if (metricsData) {
          var metricsDataJSON = JSON.parse(metricsData);
          var eventData = metricsDataJSON.data;
          _MetricsTracker2.track({
            name: metricsDataJSON.name || _enums.default.ELEMENT_INTERACTION,
            data: eventData
          });
        }
      }
    });
    // Add the mandatory dataAnalytics subscriber
    _MetricsTracker2.addSubscriber(_DataAnalyticsMetricsSubscriber.default);
    // Add the mandatory logging subscriber
    _MetricsTracker2.addSubscriber(_LoggingMetricsSubscriber.default);
    // Add Google Tag Manager dataLayer subscriber
    _MetricsTracker2.addSubscriber(_GTMMeticsSubscriber.default);
    // Add Facebook Pixel subscriber
    _MetricsTracker2.addSubscriber(_FacebookPixelSubscriber.default);
    // Add Amplitude Subscriber
    _MetricsTracker2.addSubscriber(getAmplitudeSubscriber(root));
    setupAutomaticEvents(_MetricsTracker2);
  }
}
function setupAutomaticEvents(MetricsTracker) {
  var clickableLinks = ['.wds-button', '.wds-link', 'a', 'wds-switch__native', 'wds-checkbox__native', 'wds-radio__native', 'wds-list__item', 'wds-close', 'wds-popout__trigger', 'wds-has-menu', 'wds-slider__handle', 'wds-tab__tab-btn'];
  delegatedEvents.on('click', clickableLinks.join(','), function gce(event) {
    if (!event.target) {
      return;
    }
    var name = event.type;
    var target = event.target;
    var inputTarget = target;
    MetricsTracker.automaticTrack({
      name: _enums.default.ELEMENT_INTERACTION,
      data: {
        actionType: name,
        actionFlow: 'automatic',
        class: target.className,
        text: target.innerText,
        checked: inputTarget.checked || undefined,
        value: inputTarget.value || undefined
      }
    });
  });
}
var MetricsTracker = exports.MetricsTracker = _MetricsTracker;
//# sourceMappingURL=MetricsTracker.js.map